import React, { useState, useEffect } from 'react';
import { Client, University } from './types/index.ts';
import { StepIndicator } from './components/stepIndicator.tsx';
import { ErrorMessage } from './components/ErrorMessage.tsx';
import { LoadingOverlay } from './components/LoadingOverlay.tsx';
import { MainSelectionPage } from './components/MainSelectionPage.tsx';
import { UniversityStep } from './components/steps/UniversityStep.tsx';
import { CourseStep } from './components/steps/CourseStep.tsx';
import { PlanStep } from './components/steps/PlanStep.tsx';
import { DetailsStep } from './components/steps/DetailsStep.tsx';
import { AuthStep } from './components/steps/AuthStep.tsx';
import { PaymentStep } from './components/steps/PaymentStep.tsx';
import { SuccessStep } from './components/steps/SuccessStep.tsx';
import { BookingWizard } from './components/booking/BookingWizard.tsx';
import './styles/globals.css';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { PaymentPage } from './components/payment/PaymentPage.tsx';
import { PaymentSuccess } from './components/payment/PaymentSuccess.tsx';
import { PaymentError } from './components/payment/PaymentError.tsx';
import { PayFirstSuccess } from './components/payment/PayFirstSuccess.tsx';
import { PayFirstFailed } from './components/payment/PayFirstFailed.tsx';
import { GradeSubmissionPage } from './components/grade/GradeSubmissionPage.tsx';
import { QuizPaymentPage } from './components/payment/QuizPaymentPage.tsx';
import { ApplyPage } from './components/apply/ApplyPage.tsx';
import momtazLogo from './momtaz.png';

const saveTaskData = (data: any) => {
  sessionStorage.setItem('taskData', JSON.stringify(data));
};

const loadTaskData = () => {
  const saved = sessionStorage.getItem('taskData');
  return saved ? JSON.parse(saved) : null;
};
const clearTaskData = () => {
  sessionStorage.removeItem('taskData');
};

type AppMode = 'selection' | 'task-purchase' | 'booking';

function App() {
  const [mode, setMode] = useState<AppMode>('selection');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [client, setClient] = useState<Client | null>(null);
  const [taskData, setTaskData] = useState<any>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  // Selected data - updated to use University instead of Department
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [selectedDeliverables, setSelectedDeliverables] = useState<string[]>([]);
  const [planDetails, setPlanDetails] = useState({
    description: '',
    student_name: '',
    university_id: '',
    section: '',
    topic: ''
  });
  const [paymentBreakdown, setPaymentBreakdown] = useState<any>(null);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);

  // Save task data whenever it changes (only for task purchase mode)
  useEffect(() => {
    if (mode === 'task-purchase' && (selectedCourse || selectedPlan || selectedDeliverables.length > 0)) {
      saveTaskData({
        selectedUniversity,
        selectedCourse,
        selectedPlan,
        selectedDeliverables,
        planDetails,
        paymentBreakdown,
        calculatedPrice,
        client,
        appliedCoupon
      });
    }
  }, [mode, selectedUniversity, selectedCourse, selectedPlan, selectedDeliverables, planDetails, paymentBreakdown, calculatedPrice, client, appliedCoupon]);

  // Check for payment success on mount (only for task purchase)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const taskId = urlParams.get('task_id');
    
   if (paymentStatus === 'success' && taskId) {
  // Load saved task data
  const savedData = loadTaskData();
  if (savedData) {
    setSelectedUniversity(savedData.selectedUniversity);
    setSelectedCourse(savedData.selectedCourse);
    setSelectedPlan(savedData.selectedPlan);
    setSelectedDeliverables(savedData.selectedDeliverables || []);
    setPlanDetails(savedData.planDetails || {
      description: '',
      student_name: '',
      university_id: '',
      section: '',
      topic: ''
    });
    setPaymentBreakdown(savedData.paymentBreakdown);
    setCalculatedPrice(savedData.calculatedPrice || 0);
    setClient(savedData.client);
     setAppliedCoupon(savedData.appliedCoupon || null);
  }
  
  // Get payment details from URL
  const publicTaskId = urlParams.get('public_task_id');
  const creditUsed = parseFloat(urlParams.get('credit_used') || '0');
  const externalPaid = parseFloat(urlParams.get('external_paid') || '0');
  const totalPrice = parseFloat(urlParams.get('total_price') || '0');
  
  setTaskData({
    id: parseInt(taskId),
    public_id: publicTaskId || `TASK-${taskId}`,
    payment_breakdown: {
      total_price: totalPrice,
      credit_used: creditUsed,
      external_payment: externalPaid,
      balance_used: 0,
      payment_method: creditUsed > 0 && externalPaid > 0 ? 'mixed' : creditUsed > 0 ? 'credit' : 'external'
    }
  });
  
  setMode('task-purchase');
  setStep(7);
  window.history.replaceState({}, document.title, window.location.pathname);
}
    
    else if (paymentStatus === 'failed') {
      setError('فشلت عملية الدفع. يرجى المحاولة مرة أخرى.');
      // Restore data and go back to payment step
      const savedData = loadTaskData();
      if (savedData) {
        setSelectedUniversity(savedData.selectedUniversity);
        setSelectedCourse(savedData.selectedCourse);
        setSelectedPlan(savedData.selectedPlan);
        setSelectedDeliverables(savedData.selectedDeliverables || []);
        setPlanDetails(savedData.planDetails || {
          description: '',
          student_name: '',
          university_id: '',
          section: '',
          topic: ''
        });
        setPaymentBreakdown(savedData.paymentBreakdown);
        setCalculatedPrice(savedData.calculatedPrice || 0);
        setClient(savedData.client);
        setAppliedCoupon(savedData.appliedCoupon || null);
        setMode('task-purchase');
        setStep(6); // Go back to payment step
      }
    } else if (paymentStatus === 'error') {
      setError('خطأ في معالجة الدفع. يرجى الاتصال بالدعم الفني.');
    }
  }, []);

  const resetToSelection = () => {
    setMode('selection');
    setStep(1);
    setSelectedUniversity(null);
    setSelectedCourse(null);
    setSelectedPlan(null);
    setSelectedDeliverables([]);
    setCalculatedPrice(0);
    setPlanDetails({
      description: '',
      student_name: '',
      university_id: '',
      section: '',
      topic: ''
    });
    setPaymentBreakdown(null);
    setClient(null);
    setTaskData(null);
    setAppliedCoupon(null);
    setError('');
    clearTaskData();
  };

  const resetForm = () => {
    setStep(1);
    setSelectedUniversity(null);
    setSelectedCourse(null);
    setSelectedPlan(null);
    setSelectedDeliverables([]);
    setCalculatedPrice(0);
    setPlanDetails({
      description: '',
      student_name: '',
      university_id: '',
      section: '',
      topic: ''
    });
    setPaymentBreakdown(null);
    setClient(null);
    setAppliedCoupon(null);
    setTaskData(null);
    clearTaskData();
  };

  const handleSelectTaskPurchase = () => {
    setMode('task-purchase');
    setStep(1);
    setError('');
  };

  const handleSelectBooking = () => {
    setMode('booking');
    setError('');
  };

  const renderTaskPurchaseStep = () => {
    switch (Math.floor(step)) {
      case 1:
        return (
          <UniversityStep
            onSelect={(university: University) => {
              setSelectedUniversity(university);
              setStep(2);
            }}
            setError={setError}
            setLoading={setLoading}
          />
        );
      case 2:
        return (
          <CourseStep
            selectedUniversity={selectedUniversity!}
            onSelect={(course: any) => {
              setSelectedCourse(course);
              setStep(3);
            }}
            onBack={() => setStep(1)}
            setError={setError}
            setLoading={setLoading}
          />
        );
      case 3:
        return (
          <PlanStep
            selectedCourse={selectedCourse}
            selectedPlan={selectedPlan}
            selectedDeliverables={selectedDeliverables}
            calculatedPrice={calculatedPrice}
            onSelectPlan={setSelectedPlan}
            onSelectDeliverables={setSelectedDeliverables}
            onCalculatedPrice={setCalculatedPrice}
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
            setError={setError}
            setLoading={setLoading}
          />
        );
      case 4:
        return (
          <DetailsStep
            selectedPlan={selectedPlan}
            planDetails={planDetails}
            onDetailsChange={setPlanDetails}
            onNext={() => setStep(5)}
            onBack={() => setStep(3)}
          />
        );
      case 5:
        return (
          <AuthStep
            onAuthenticated={(authClient: Client) => {
              setClient(authClient);
              setStep(6);
            }}
            onBack={() => setStep(4)}
            setError={setError}
            setLoading={setLoading}
            selectedPlan={selectedPlan}
            selectedDeliverables={selectedDeliverables}
            setPaymentBreakdown={setPaymentBreakdown}
            step={step}
            setStep={setStep}
          />
        );
      case 6:
        return (
          <PaymentStep
            client={client!}
            selectedCourse={selectedCourse}
            selectedPlan={selectedPlan}
            selectedDeliverables={selectedDeliverables}
            paymentBreakdown={paymentBreakdown}
            calculatedPrice={calculatedPrice}
            planDetails={planDetails}
            onSuccess={(createdTaskData) => {
              // Save the task data if provided
              if (createdTaskData) {
                setTaskData(createdTaskData);
              }
              clearTaskData();
              setStep(7);
            }}
            onBack={() => setStep(5)}
            setError={setError}
            setLoading={setLoading}
            sessionId={sessionId}
            appliedCoupon={appliedCoupon} 
            onCouponChange={setAppliedCoupon} 
          />
        );
      case 7:
        return (
          <SuccessStep
            selectedCourse={selectedCourse}
            selectedPlan={selectedPlan}
            selectedDeliverables={selectedDeliverables}
            paymentBreakdown={paymentBreakdown}
            calculatedPrice={calculatedPrice}
            taskData={taskData}
            onCreateAnother={resetForm}
          />
        );
      default:
        return null;
    }
  };

  // Render main content based on mode
  const renderContent = () => {
    switch (mode) {
      case 'selection':
        return (
          <MainSelectionPage
            onSelectTaskPurchase={handleSelectTaskPurchase}
            onSelectBooking={handleSelectBooking}
          />
        );
      
      case 'task-purchase':
        return (
          <div className="max-w-lg mx-auto px-4 py-6 pb-24 md:pb-12">
            {/* Back to selection button */}
            <div className="mb-4">
              <button
                onClick={resetToSelection}
                className="flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                العودة للقائمة الرئيسية
              </button>
            </div>
            
            <StepIndicator currentStep={step} />
            {renderTaskPurchaseStep()}
          </div>
        );
      
      case 'booking':
        return (
          <div>
            {/* Back to selection button */}
            <div className="max-w-4xl mx-auto px-4 pt-4">
              <button
                onClick={resetToSelection}
                className="flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                العودة للقائمة الرئيسية
              </button>
            </div>
            
            <BookingWizard />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
  <Router>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50" dir="rtl">
      <LoadingOverlay loading={loading} />
      
      <Routes>
          <Route path="/test-route" element={<div style={{padding: '50px', fontSize: '24px'}}>TEST ROUTE WORKS!</div>} />

        {/* Your existing main app route */}
        <Route path="/" element={
          <>
            {/* Header */}
            {mode === 'selection' && (
              <header className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <img src={momtazLogo} alt="Momtaz Logo" className="h-14 w-14 rounded-lg" />
                      <div>
                        <div className="text-2xl font-bold text-gray-900">Kuwait Best Solver</div>
                        <div className="text-sm text-teal-700 font-medium">خدمة من ممتاز</div>
                      </div>
                    </div>
                    <nav className="hidden md:flex space-x-8 space-x-reverse">
                      <a href="#" className="text-gray-600 hover:text-gray-900">الرئيسية</a>
                    </nav>
                  </div>
                </div>
              </header>
            )}
            
            <ErrorMessage error={error} onClose={() => setError('')} />
            {renderContent()}
            
            {/* Footer */}
            {mode === 'selection' && (
              <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <img src={momtazLogo} alt="Momtaz Logo" className="h-12 w-12 rounded-lg" />
                        <div>
                          <h3 className="text-xl font-bold">Kuwait Best Solver</h3>
                          <span className="text-sm text-teal-400">خدمة من ممتاز</span>
                        </div>
                      </div>
                      <p className="text-gray-400">
                        نقدم أفضل الخدمات التعليمية للطلاب الجامعيين
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-4">الخدمات</h4>
                      <ul className="space-y-2 text-gray-400">
                        <li>شراء المهام</li>
                        <li>حجز الجلسات</li>
                        <li>الاستشارات الأكاديمية</li>
                        <li>مراجعة الواجبات</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-4">الدعم</h4>
                      <ul className="space-y-2 text-gray-400">
                        <li>مركز المساعدة</li>
                        <li>اتصل بنا</li>
                        <li>الأسئلة الشائعة</li>
                        <li>سياسة الخصوصية</li>
                      </ul>
                    </div>
                  </div>
                  <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
                    <p>&copy;{new Date().getFullYear()} Momtaz - Kuwait Best Solver. جميع الحقوق محفوظة.</p>
                  </div>
                </div>
              </footer>
            )}
          </>
        } />
        
        {/* NEW: Payment routes - completely separate pages */}
        <Route path="/pay/:token" element={<PaymentPage />} />
        <Route path="/pay/:token/success" element={<PaymentSuccess />} />
        <Route path="/pay/:token/error" element={<PaymentError />} />

        {/* Pay First (pre-payment task creation) routes */}
        <Route path="/payment-success/:taskId" element={<PayFirstSuccess />} />
        <Route path="/payment-failed" element={<PayFirstFailed />} />

        {/* Quiz credit payment choice page (trusted clients) */}
        <Route path="/quiz-payment/:token" element={<QuizPaymentPage />} />

        {/* Grade submission routes */}
        <Route path="/grade/:token/:type" element={<GradeSubmissionPage />} />

        {/* Job application page */}
        <Route path="/apply" element={<ApplyPage />} />
      </Routes>
    </div>
  </Router>
);
}

export default App;